import { copyToClipboard } from "../../utils/common";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Modal,
} from "@mui/material";
import { useHeaderTitle } from "../../contexts/HeaderTitleContext";
import { useNavigate, useParams } from "react-router-dom";
import { nip19, Event as NostrEvent } from "nostr-tools";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import { useTheme } from "@mui/material/styles";
import { useDMContext } from "../../hooks/useDMContext";
import { useAppContext } from "../../hooks/useAppContext";
import { useUserContext } from "../../hooks/useUserContext";
import { getConversationId, fetchInboxRelays } from "../../nostr/nip17";
import { DEFAULT_IMAGE_URL } from "../../utils/constants";
import { DMMessage, SendTracking } from "../../contexts/dm-context";
import { dataLayer, type PublishResult } from "@formstr/local-relay";
import { mergePublishResults } from "../../nostr/nip17";
import MessageBubble from "./MessageBubble";
import MessageContextMenu from "./MessageContextMenu";
import MessageInput from "./MessageInput";

export type RelayStatus = "pending" | "sent" | "failed" | "timeout";

export interface MsgSendStatus {
  relays: Record<string, RelayStatus>;
  reasons: Record<string, string>; // relay url -> rejection reason from relay
  latencies: Record<string, number>; // relay url -> ms to respond
  retryWraps: NostrEvent[]; // signed gift wraps, kept so retry can republish
}

// The worker reports a PublishStatus per relay; map it to the UI's RelayStatus.
const STATUS_MAP: Record<string, RelayStatus> = {
  accepted: "sent",
  rejected: "failed",
  timeout: "timeout",
  failed: "failed",
};


const ChatView: React.FC = () => {
  const { npub } = useParams<{ npub: string }>();
  const navigate = useNavigate();
  const { conversations, sendMessage, sendReaction, markAsRead } =
    useDMContext();
  const { profiles, fetchUserProfileThrottled } = useAppContext();
  const { user } = useUserContext();
  const [contextMenuMsg, setContextMenuMsg] = useState<DMMessage | null>(null);
  const [replyTo, setReplyTo] = useState<DMMessage | null>(null);
  const [pickerForMsgId, setPickerForMsgId] = useState<string | null>(null);
  const [sendStatuses, setSendStatuses] = useState<Map<string, MsgSendStatus>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Decode npub to pubkey
  let recipientPubkey: string | null = null;
  try {
    if (npub) {
      const decoded = nip19.decode(npub);
      if (decoded.type === "npub") {
        recipientPubkey = decoded.data;
      } else if (decoded.type === "nprofile") {
        recipientPubkey = decoded.data.pubkey;
      }
    }
  } catch {
    // invalid npub
  }

  const conversationId =
    user && recipientPubkey
      ? getConversationId(user.pubkey, [recipientPubkey])
      : null;
  const conversation = conversationId
    ? conversations.get(conversationId)
    : null;

  useEffect(() => {
    if (recipientPubkey && !profiles?.get(recipientPubkey)) {
      fetchUserProfileThrottled(recipientPubkey);
    }
  }, [recipientPubkey, profiles, fetchUserProfileThrottled]);

  // Warm the inbox relay cache for both parties as soon as the chat opens,
  // so the relay lookup is already resolved by the time the user hits send.
  // persist=true only for the logged-in user — their relays are saved to localStorage.
  useEffect(() => {
    if (recipientPubkey && user?.pubkey) {
      fetchInboxRelays(recipientPubkey);
      fetchInboxRelays(user.pubkey, true);
    }
  }, [recipientPubkey, user?.pubkey]);

  useEffect(() => {
    if (conversationId && conversation && conversation.unreadCount > 0) {
      markAsRead(conversationId);
    }
  }, [conversationId, conversation, markAsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages?.length]);

  // The worker fans each gift wrap out to the relays it owns and reports the
  // per-relay outcome in the PublishResult. We render that directly — there is
  // no app-side per-relay promise to race against a timeout anymore.
  const applyResult = useCallback(
    (rumorId: string, result: PublishResult, wraps: NostrEvent[], onlyRelays?: string[]) => {
      setSendStatuses(prev => {
        const s = prev.get(rumorId);
        const relays: Record<string, RelayStatus> = { ...(s?.relays ?? {}) };
        const reasons: Record<string, string> = { ...(s?.reasons ?? {}) };
        const latencies: Record<string, number> = { ...(s?.latencies ?? {}) };
        for (const r of result.relayResults) {
          if (onlyRelays && !onlyRelays.includes(r.relay)) continue;
          relays[r.relay] = STATUS_MAP[r.status] ?? "failed";
          if (r.message) reasons[r.relay] = r.message;
          latencies[r.relay] = r.latencyMs;
        }
        return new Map(prev).set(rumorId, { relays, reasons, latencies, retryWraps: wraps });
      });
    },
    []
  );

  const trackRelays = useCallback((tracking: SendTracking) => {
    applyResult(tracking.rumorId, tracking.result, tracking.wraps);
  }, [applyResult]);

  const handleRetry = useCallback(async (rumorId: string, relay?: string) => {
    const status = sendStatuses.get(rumorId);
    if (!status) return;

    const relaysToRetry = relay
      ? [relay]
      : Object.keys(status.relays).filter(r => status.relays[r] !== "sent");

    // Show the retried relays as pending while the worker re-attempts delivery.
    setSendStatuses(prev => {
      const s = prev.get(rumorId);
      if (!s) return prev;
      const reset = Object.fromEntries(relaysToRetry.map(r => [r, "pending" as RelayStatus]));
      const reasons = Object.fromEntries(
        Object.entries(s.reasons).filter(([r]) => !relaysToRetry.includes(r))
      );
      return new Map(prev).set(rumorId, { ...s, relays: { ...s.relays, ...reset }, reasons });
    });

    // Republish the stored wraps — the worker owns relay selection/delivery.
    const results = await Promise.all(
      status.retryWraps.map(w => dataLayer.publishEvent(w))
    );
    applyResult(rumorId, mergePublishResults(results), status.retryWraps, relaysToRetry);
  }, [sendStatuses, applyResult]);

  // Called by MessageInput — throwing here causes MessageInput to restore the draft
  const handleSend = useCallback(async (content: string) => {
    if (!recipientPubkey) throw new Error("No recipient");
    const tracking = await sendMessage(recipientPubkey, content, replyTo?.id);
    setReplyTo(null);
    trackRelays(tracking);
  }, [recipientPubkey, sendMessage, replyTo, trackRelays]);

  const handleReaction = useCallback(
    async (emoji: string, messageId: string) => {
      if (!recipientPubkey) return;
      try {
        await sendReaction(recipientPubkey, emoji, messageId);
      } catch (e) {
        console.error("Failed to send reaction:", e);
      }
    },
    [recipientPubkey, sendReaction],
  );

  // The contact is what's in focus, so it goes in the block header.
  const headerProfile = recipientPubkey ? profiles?.get(recipientPubkey) : undefined;
  const headerName = recipientPubkey
    ? headerProfile?.display_name ||
      headerProfile?.name ||
      nip19.npubEncode(recipientPubkey).slice(0, 12) + "..."
    : "";
  const headerPicture = headerProfile?.picture || DEFAULT_IMAGE_URL;
  const headerTitle = useMemo(
    () =>
      recipientPubkey ? (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/profile/${nip19.npubEncode(recipientPubkey)}`)}
          sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer", minWidth: 0 }}
        >
          <Avatar src={headerPicture} sx={{ width: 36, height: 36, boxShadow: "0 0 0 2px currentColor" }} />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {headerName}
          </Box>
        </Box>
      ) : null,
    [recipientPubkey, headerName, headerPicture, navigate]
  );
  useHeaderTitle(headerTitle);

  if (!recipientPubkey) {
    return (
      <Box maxWidth={800} mx="auto" px={2} py={4}>
        <Typography color="error">Invalid recipient</Typography>
      </Box>
    );
  }

  const recipientProfile = profiles?.get(recipientPubkey);
  const recipientName =
    recipientProfile?.display_name ||
    recipientProfile?.name ||
    nip19.npubEncode(recipientPubkey).slice(0, 12) + "...";

  const messages = conversation?.messages || [];

  return (
    <Box
      maxWidth={800}
      mx="auto"
      display="flex"
      flexDirection="column"
      height="100%"
      minHeight={0}
    >
      {/* Messages area */}
      <Box
        flex={1}
        minHeight={0}
        overflow="auto"
        px={2}
        py={1}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        {messages.length === 0 && (
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="body2" color="text.secondary">
              No messages yet. Say hello!
            </Typography>
          </Box>
        )}
        {messages.map((msg) => {
          const isMine = msg.pubkey === user?.pubkey;
          const msgReactions = conversation?.reactions?.get(msg.id) || [];
          const groupedReactions = msgReactions.reduce<
            Record<
              string,
              { emoji: string; count: number; pubkeys: string[]; tags?: string[][] }
            >
          >((acc, r) => {
            if (!acc[r.emoji]) {
              acc[r.emoji] = { emoji: r.emoji, count: 0, pubkeys: [], tags: r.tags };
            }
            acc[r.emoji].count++;
            acc[r.emoji].pubkeys.push(r.pubkey);
            return acc;
          }, {});

          const replyTag = msg.tags.find(
            (t) => t[0] === "e" && t[3] === "reply"
          );
          const referencedMsg = replyTag
            ? messages.find((m) => m.id === replyTag[1])
            : undefined;
          const referencedMsgSenderName = referencedMsg
            ? referencedMsg.pubkey === user?.pubkey
              ? "You"
              : recipientName
            : undefined;

          return (
            <MessageBubble
              key={msg.id}
              msg={msg}
              isMine={isMine}
              reactions={groupedReactions}
              referencedMsg={referencedMsg}
              referencedMsgSenderName={referencedMsgSenderName}
              sendStatus={sendStatuses.get(msg.id)}
              onLongPress={setContextMenuMsg}
              onReact={handleReaction}
              onSwipeReply={setReplyTo}
              onRetry={(relay) => handleRetry(msg.id, relay)}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      <MessageInput
        replyTo={replyTo}
        replyToSenderName={
          replyTo
            ? replyTo.pubkey === user?.pubkey ? "You" : recipientName
            : undefined
        }
        onClearReply={() => setReplyTo(null)}
        onSend={handleSend}
      />

      {/* Context menu */}
      <MessageContextMenu
        msg={contextMenuMsg}
        onClose={() => setContextMenuMsg(null)}
        onReact={handleReaction}
        onReply={setReplyTo}
        onCopy={(content) => copyToClipboard(content)}
        onOpenEmojiPicker={setPickerForMsgId}
      />

      {/* Full emoji picker — single instance, shared across all messages */}
      <Modal
        open={Boolean(pickerForMsgId)}
        onClose={() => setPickerForMsgId(null)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            overscrollBehavior: "contain",
            touchAction: "pan-y",
          }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <EmojiPicker
            // Device-font emojis — no CDN sprite fetch, instant open, offline-safe.
            emojiStyle={EmojiStyle.NATIVE}
            lazyLoadEmojis={false}
            theme={
              theme.palette.mode === "light"
                ? ("light" as Theme)
                : ("dark" as Theme)
            }
            onEmojiClick={(emojiData) => {
              if (pickerForMsgId) handleReaction(emojiData.emoji, pickerForMsgId);
              setPickerForMsgId(null);
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ChatView;
